import type {
  BlocksUser,
  InviteUserPayload,
  UsersResponse,
} from "@/src/models/user.model";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";
import { BLOCKS_USER_QUERY_KEYS } from "../query-keys/users.keys";
import { COMMENTS_QUERY_KEY, POSTS_QUERY_KEY } from "../query-keys/post.keys";
import { NEWS_QUERY_KEY } from "../query-keys/news.keys";
import {
  EVENTS_QUERY_KEY,
  EVENT_GALLERY_QUERY_KEY,
  RSVPS_QUERY_KEY,
} from "../query-keys/events.keys";
import { decodeToken } from "./api/auth.api";
import apiClient from "./api/axios";
import { purgeUserData } from "./user-data-purge";

const PAGE_SIZE = 10;
const ALL_PAGE_SIZE = 1000000;
const BATCH_SIZE = 20;
const PROJECT_KEY = process.env.EXPO_PUBLIC_X_BLOCKS_KEY;

const fetchUsers = async (
  page: number,
  search: string,
  startDate?: string,
  searchBy: "name" | "email" = "name",
  pageSize: number = PAGE_SIZE,
) => {
  const decoded = await decodeToken();
  const currentOrgId = decoded?.org_id;

  return apiClient.post("/idp/v1/Iam/GetUsers", {
    page,
    pageSize,
    projectKey: PROJECT_KEY,
    sort: { property: "CreatedDate", isDescending: false },
    filter: {
      name: searchBy === "name" ? search : "",
      email: searchBy === "email" ? search : "",
      ...(startDate && { joinedOn: startDate }),
      ...(currentOrgId && { organizationId: currentOrgId }),
    },
  });
};


export const useGetActiveUsers = () => {
  return useQuery({
    queryKey: ['activeUsers'],
    queryFn: async () => {
      const decoded = await decodeToken();
      const currentOrgId = decoded?.org_id;

      return apiClient.post("/idp/v1/Iam/GetUsers", {
        projectKey: PROJECT_KEY,
        filter: {
          name: "",
          email: "",
          status: {
            active: true,
            inactive: false
          },
          organizationId: currentOrgId
        },
      });
    },
  });
};

const filterByEndDate = (
  users: ReadonlyArray<BlocksUser>,
  endDate: string,
): ReadonlyArray<BlocksUser> => {
  const endDateTime = new Date(endDate);
  endDateTime.setHours(23, 59, 59, 999);
  return users.filter(
    (user) => new Date(user.createdDate).getTime() <= endDateTime.getTime(),
  );
};

export const fetchBlocksUsersByIds = async (
  userIds: ReadonlyArray<string>,
): Promise<Record<string, BlocksUser>> => {
  const uniqueUserIds = [...new Set(userIds)];
  const usersMap: Record<string, BlocksUser> = {};
  const decoded = await decodeToken();
  const currentOrgId = decoded?.org_id;

  for (let i = 0; i < uniqueUserIds.length; i += BATCH_SIZE) {
    const batch = uniqueUserIds.slice(i, i + BATCH_SIZE);
    try {
      const response = await apiClient.post("/idp/v1/Iam/GetUsers", {
        page: 0,
        pageSize: BATCH_SIZE,
        projectKey: PROJECT_KEY,
        filter: {
          name: "",
          email: "",
          // GetUsers filters by `userIds` (matches every other call in the app);
          // `itemIds` is ignored by the API, so the batch came back without the
          // requested users and the leaderboard fell back to showing raw ids.
          userIds: batch,
          ...(currentOrgId && { organizationId: currentOrgId }),
        },
      });
      const users: BlocksUser[] = response.data?.data ?? [];
      users.forEach((blocksUser) => {
        usersMap[blocksUser.itemId] = blocksUser;
      });
    } catch {}
  }

  return usersMap;
};

let avatarBatchIds = new Set<string>();
let avatarBatchPromise: Promise<Record<string, BlocksUser>> | null = null;

const loadBlocksUser = (userId: string): Promise<BlocksUser | undefined> => {
  avatarBatchIds.add(userId);
  if (!avatarBatchPromise) {
    avatarBatchPromise = new Promise((resolve) => {
      setTimeout(async () => {
        const ids = [...avatarBatchIds];
        avatarBatchIds = new Set();
        avatarBatchPromise = null;
        resolve(await fetchBlocksUsersByIds(ids));
      }, 50);
    });
  }
  return avatarBatchPromise.then((map) => map[userId]);
};

export const useBlocksUser = (userId?: string) =>
  useQuery({
    queryKey: BLOCKS_USER_QUERY_KEYS.userById(userId ?? ""),
    queryFn: () => loadBlocksUser(userId as string),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });

export const useUsersQuery = (
  page: number = 0,
  search: string = "",
  startDate?: string,
  endDate?: string,
) =>
  useQuery<UsersResponse>({
    queryKey: BLOCKS_USER_QUERY_KEYS.users(page, search, startDate, endDate),
    queryFn: async () => {
      const nameResponse = await fetchUsers(
        page,
        search,
        startDate,
        "name",
        PAGE_SIZE,
      );
      let users: ReadonlyArray<BlocksUser> = nameResponse.data.data;
      let totalCount: number = nameResponse.data.totalCount;

      if (users.length === 0 && search.trim() !== "") {
        const emailResponse = await fetchUsers(
          page,
          search,
          startDate,
          "email",
          PAGE_SIZE,
        );
        users = emailResponse.data.data;
        totalCount = emailResponse.data.totalCount;
      }

      return {
        users: endDate ? filterByEndDate(users, endDate) : users,
        totalCount,
        pageSize: PAGE_SIZE,
      };
    },
    placeholderData: (previousData) => previousData,
  });

export const useAllUsersQuery = (
  search: string = "",
  startDate?: string,
  endDate?: string,
  enabled: boolean = false,
) =>
  useQuery<UsersResponse>({
    queryKey: BLOCKS_USER_QUERY_KEYS.allUsers(search, startDate, endDate),
    queryFn: async () => {
      const nameResponse = await fetchUsers(
        0,
        search,
        startDate,
        "name",
        ALL_PAGE_SIZE,
      );
      let users: ReadonlyArray<BlocksUser> = nameResponse.data.data;
      let totalCount: number = nameResponse.data.totalCount;

      if (users.length === 0 && search.trim() !== "") {
        const emailResponse = await fetchUsers(
          0,
          search,
          startDate,
          "email",
          ALL_PAGE_SIZE,
        );
        users = emailResponse.data.data;
        totalCount = emailResponse.data.totalCount;
      }

      return {
        users: endDate ? filterByEndDate(users, endDate) : users,
        totalCount,
        pageSize: ALL_PAGE_SIZE,
      };
    },
    enabled,
    placeholderData: (previousData) => previousData,
  });

const inviteUser = async (payload: InviteUserPayload) => {
  const decoded = await decodeToken();
  const currentOrgId = decoded?.org_id;

  return apiClient.post("/idp/v1/Iam/Create", {
    email: payload.email,
    firstName: payload.firstName,
    lastName: payload.lastName,
    userPassType: 1,
    userCreationType: 1,
    platform: "blocks_portal",
    projectKey: PROJECT_KEY,
    organizationId: currentOrgId,

    memberships: [
      {
        organizationId: currentOrgId,
        roles: [payload.role ?? "member"],
        permissions: [],
      },
      ...(currentOrgId !== "default"
        ? [
            {
              organizationId: "default",
              roles: ["user"],
              permissions: [],
            },
          ]
        : []),
    ],
  });
};

export const useInviteUsers = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const inviteUsers = useMutation({
    mutationFn: (users: InviteUserPayload[]) =>
      Promise.all(users.map(inviteUser)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BLOCKS_USER_QUERY_KEYS.usersBase,
      });
      Toast.show({ type: "success", text1: t("SUCCESS_USERS_INVITED") });
    },
    onError: (error: any) => {
      const serverErrors = error?.response?.data?.errors;
      const emailError = serverErrors?.Email;

      Toast.show({
        type: "error",
        text1: emailError ?? t("ERROR_INVITE_USERS"),
      });
    },
  });

  return { inviteUsers };
};

export const useDeleteUserMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiClient.post("/idp/v1/Iam/Deactivate", {
        userId,
        projectKey: PROJECT_KEY,
      });
      await purgeUserData(userId);

      return res;
    },
    onSuccess: () => {
      // Refresh the user list plus every feature surface the purge touched.
      queryClient.invalidateQueries({
        queryKey: BLOCKS_USER_QUERY_KEYS.usersBase,
      });
      [
        POSTS_QUERY_KEY,
        COMMENTS_QUERY_KEY,
        NEWS_QUERY_KEY,
        ["leaderboard"],
        [EVENTS_QUERY_KEY],
        [RSVPS_QUERY_KEY],
        [EVENT_GALLERY_QUERY_KEY],
      ].forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
      Toast.show({ type: "success", text1: t("SUCCESS_USER_DELETED") });
    },
    onError: () => {
      Toast.show({ type: "error", text1: t("ERROR_USER_STATUS_TOGGLED") });
    },
  });
};

// Deactivate = only flip the account inactive. No data purge, no other
export const useDeactivateUserMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (userId: string) =>
      apiClient.post("/idp/v1/Iam/Deactivate", {
        userId,
        projectKey: PROJECT_KEY,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BLOCKS_USER_QUERY_KEYS.usersBase,
      });
      Toast.show({ type: "success", text1: t("SUCCESS_USER_DEACTIVATED") });
    },
    onError: () => {
      Toast.show({ type: "error", text1: t("ERROR_USER_STATUS_TOGGLED") });
    },
  });
};

export const useSendActivationToUser = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (userId: string) =>
      apiClient.post("/idp/v1/Iam/ResendActivation", {
        userId,
        projectKey: PROJECT_KEY,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BLOCKS_USER_QUERY_KEYS.usersBase,
      });
      Toast.show({ type: "success", text1: t("SUCCESS_USER_DEACTIVATED") });
    },
    onError: () => {
      Toast.show({ type: "error", text1: t("ERROR_USER_STATUS_TOGGLED") });
    },
  });
};

export const useBlocksUserByIdQuery = (
  userId: string,
  options?: { enabled?: boolean },
) =>
  useQuery<BlocksUser>({
    queryKey: BLOCKS_USER_QUERY_KEYS.userById(userId),
    queryFn: async () => {
      const response = await apiClient.get("/idp/v1/Iam/GetUser", {
        params: {
          Id: userId,
          ProjectKey: PROJECT_KEY,
        },
      });
      return response.data.data;
    },
    enabled: options?.enabled ?? true,
  });

export type UpdateBlocksUserInput = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  profileImageUrl: string | null;
  // Roles to set for `orgId` only; other orgs' memberships are preserved.
  roles?: string[];
  orgId?: string;
};

export const useUpdateBlocksUserMutation = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({
      userId,
      input,
    }: {
      userId: string;
      input: UpdateBlocksUserInput;
    }) => {
      const currentResponse = await apiClient.get("/idp/v1/Iam/GetUser", {
        params: { Id: userId, ProjectKey: PROJECT_KEY },
      });
      const current = currentResponse.data.data;

      const response = await apiClient.post("/idp/v1/Iam/Update", {
        lastLoggedInTime: current.lastLoggedInTime,
        lastLoggedInDeviceInfo: current.lastLoggedInDeviceInfo,
        logInCount: current.logInCount,
        itemId: current.itemId,
        createdDate: current.createdDate,
        lastUpdatedDate: current.lastUpdatedDate,
        language: current.language,
        salutation: current.salutation,
        email: current.email,
        userName: current.userName,
        active: current.active,
        isVarified: current.isVarified,
        mfaEnabled: current.mfaEnabled,
        isMfaVerified: current.isMfaVerified,
        userMfaType: current.userMfaType,
        userCreationType: current.userCreationType,
        memberships: input.roles
          ? (current.memberships ?? []).map((membership: any) =>
              membership.organizationId === input.orgId
                ? { ...membership, roles: input.roles }
                : membership,
            )
          : current.memberships,
        firstName: input.firstName,
        lastName: input.lastName,
        phoneNumber: input.phoneNumber,
        profileImageUrl: input.profileImageUrl,
        projectKey: PROJECT_KEY,
      });
      return response.data;
    },
    onSuccess: (_, { userId, input }) => {
      queryClient.setQueryData(
        BLOCKS_USER_QUERY_KEYS.userById(userId),
        (old: BlocksUser | undefined) => {
          if (!old) return old;
          return {
            ...old,
            firstName: input.firstName,
            lastName: input.lastName,
            phoneNumber: input.phoneNumber,
            profileImageUrl: input.profileImageUrl,
          };
        },
      );
      queryClient.invalidateQueries({
        queryKey: BLOCKS_USER_QUERY_KEYS.userById(userId),
        refetchType: "active",
      });
      // Refresh the user list so role/name changes show without a manual reload.
      queryClient.invalidateQueries({
        queryKey: BLOCKS_USER_QUERY_KEYS.usersBase,
      });
      Toast.show({ type: "success", text1: t("SUCCESS_PROFILE_UPDATED") });
    },
    onError: () => {
      Toast.show({ type: "error", text1: t("ERROR_PROFILE_UPDATE_FAILED") });
    },
  });
};

export type UserRole = "admin" | "member";
export type UserStatus = "active" | "inactive";
export type StatItem = {
  count: number;
  text: string;
  icon: React.ReactElement;
};

export const useUsersByIdsQuery = (userIds: string[]) =>
  useQuery<UsersResponse>({
    queryKey: BLOCKS_USER_QUERY_KEYS.usersByIds(userIds),
    enabled: userIds.length > 0,
    queryFn: async () => {
      const decoded = await decodeToken();
      const currentOrgId = decoded?.org_id;

      const response = await apiClient.post("/idp/v1/Iam/GetUsers", {
        page: 0,
        pageSize: userIds.length,
        projectKey: PROJECT_KEY,
        sort: { property: "CreatedDate", isDescending: false },
        filter: {
          name: "",
          email: "",
          userIds,
          ...(currentOrgId && { organizationId: currentOrgId }),
        },
      });

      return {
        users: response.data.data,
        totalCount: response.data.totalCount,
        pageSize: userIds.length,
      };
    },
  });
